/**
 * Utility to wait for a canvas element to be ready inside a container.
 *
 * Used by pdfGenerator to wait for offscreen UVFloorPlanCanvas to finish
 * rendering before capturing with toDataURL. Replaces a previous fixed
 * 1000ms setTimeout which could capture blank/incomplete canvases.
 *
 * UVFloorPlanCanvas draws synchronously in a useEffect, so once the
 * <canvas> element is in the DOM with non-zero dimensions, drawing
 * is complete.
 */
export function waitForCanvasReady(
  container,
  {
    maxWaitMs = 5000,
    pollIntervalMs = 100,
    // A fresh <canvas> is already 300x150, so "non-zero" only proves the
    // element exists. Callers that know the size to expect pass a stricter
    // test (see `captureOffscreenCanvas`).
    isReady = (canvas) => canvas.width > 0 && canvas.height > 0,
  } = {}
) {
  return new Promise((resolve) => {
    const startTime = Date.now();

    const check = () => {
      const canvas = container.querySelector('canvas');

      if (canvas && isReady(canvas)) {
        // Canvas exists and has dimensions — drawing has completed.
        // Wait one more frame to ensure the browser has flushed paint.
        requestAnimationFrame(() => resolve(canvas));
        return;
      }

      if (Date.now() - startTime > maxWaitMs) {
        console.warn('[canvasLoadPoller] Timed out waiting for canvas');
        resolve(canvas || null);
        return;
      }

      setTimeout(check, pollIntervalMs);
    };

    // Initial delay to let React mount and run effects
    setTimeout(check, 300);
  });
}
